import { SidebarProvider } from "./SidebarContext";
import { ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthProvider";
import { ConfirmationProvider } from "./ConfirmationProvider";
import { SubscriptionGuardProvider } from "./SubscriptionGuardProvider";
import { ToastProvider } from "./ToastNotification";
import { ThemeProvider } from "./ThemeProvider";
import { ErrorBoundary } from "./ErrorBoundary";

const publicRoutes = new Set(["/signin", "/signup", "/set-password", "/forgot-password"]);
export type AppScope = "business" | "platform-admin";

function AccessDenied() {
  const pathname = useLocation().pathname;
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="mx-auto max-w-md rounded-2xl border border-border bg-card p-10 text-center shadow-main">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-warning/10">
          <svg className="h-7 w-7 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-warning">Access Restricted</p>
        <h1 className="mt-3 text-2xl font-bold text-foreground">You don&apos;t have access to this page</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {pathname !== "/" ? (
            <>The page <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{pathname}</span> is not available for your account.</>
          ) : (
            "This section is not available for your account."
          )}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">Contact your administrator if you believe this is a mistake.</p>
        <a
          href="/"
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          ← Back to Dashboard
        </a>
      </div>
    </div>
  );
}

export function AppAccessGate({ children, appScope }: { children: ReactNode; appScope: AppScope }) {
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const { hydrated, user, canAccessRoute } = useAuth();

  // Derive the session key the same way AuthProvider does — used as a
  // secondary check to avoid redirecting while hydration is still in flight.
  const sessionKey = appScope === "platform-admin" ? "rentos_adm_session" : "rentos_biz_session";

  useEffect(() => {
    if (!hydrated) return;

    // Extra guard: if localStorage still has a session, don't redirect yet —
    // the AuthProvider may still be processing the hydration state update.
    if (!user && !publicRoutes.has(pathname)) {
      const hasStoredSession = typeof window !== "undefined" &&
        Boolean(window.localStorage.getItem(sessionKey));
      if (hasStoredSession) return; // wait for AuthProvider to finish hydrating
      navigate("/signin", { replace: true });
      return;
    }

    if (user?.mustChangePassword) {
      const changeRoute = "/set-password";
      const changeParam = "?force=1";
      if (pathname !== changeRoute) {
        navigate(`${changeRoute}${changeParam}`, { replace: true });
        return;
      }
    }

    if (user && publicRoutes.has(pathname) && !user.mustChangePassword) {
      const redirectTo = appScope === "platform-admin" ? "/console" : "/";
      navigate(redirectTo, { replace: true });
      return;
    }

    if (appScope === "platform-admin" && user?.role === "super_admin" && pathname === "/") {
      navigate("/console", { replace: true });
    }
  }, [appScope, hydrated, pathname, navigate, user, sessionKey]);

  if (!hydrated) return <div className="min-h-screen bg-background" />;
  // If hydrated but user is null — check localStorage one more time before
  // showing a blank screen. Handles the race where hydration effect hasn't
  // propagated the state update yet.
  if (!user && !publicRoutes.has(pathname)) {
    const hasStoredSession = typeof window !== "undefined" &&
      Boolean(window.localStorage.getItem(sessionKey));
    if (hasStoredSession) return <div className="min-h-screen bg-background" />;
    return <div className="min-h-screen bg-background" />;
  }

  const passwordChangeRoute = "/set-password";
  if (user?.mustChangePassword && pathname !== passwordChangeRoute) {
    return <div className="min-h-screen bg-background" />;
  }

  if (publicRoutes.has(pathname)) return <>{children}</>;

  // Role-based app access with edge case handling:
  // - Platform admin: only super_admin role
  // - Business app: staff role OR super_admin who has a businessId (edge case: promoted business owner)
  // businessId is not on the AuthUser type but is present at runtime from the API response
  const userWithBizId = user as (typeof user & { businessId?: string | null });
  const roleAllowedInApp =
    appScope === "platform-admin"
      ? user?.role === "super_admin"
      : user?.role === "staff" || (user?.role === "super_admin" && !!userWithBizId?.businessId);

  const routeBelongsToApp =
    appScope === "platform-admin"
      ? pathname.startsWith("/console") || pathname.startsWith("/admin") || pathname.startsWith("/platform")
      : !pathname.startsWith("/console") && !pathname.startsWith("/admin") && !pathname.startsWith("/platform");
  const canRenderRoute = Boolean(roleAllowedInApp && routeBelongsToApp && canAccessRoute(pathname));

  // Business app: render directly (RTK Query handles data per-page)
  if (appScope === "business") {
    return canRenderRoute ? <>{children}</> : <AccessDenied />;
  }

  // Platform-admin: no business data providers
  return canRenderRoute ? <>{children}</> : <AccessDenied />;
}

export const ClientProviders = ({
  children,
  appScope = "business",
}: {
  children: ReactNode;
  appScope?: AppScope;
}) => {
  return (
    <ErrorBoundary>
      <SidebarProvider>
        <ThemeProvider>
          <AuthProvider appScope={appScope}>
            <ToastProvider>
              <SubscriptionGuardProvider>
                <ConfirmationProvider>
                  {children}
                </ConfirmationProvider>
              </SubscriptionGuardProvider>
            </ToastProvider>
          </AuthProvider>
        </ThemeProvider>
      </SidebarProvider>
    </ErrorBoundary>
  );
};
